import fuzzysort from 'fuzzysort';
import React, {
  ChangeEvent,
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from '../../hooks/onClickOutside';
import { ReactComponent as ColourDrop } from '../../images/colour-drop.svg';
import {
  DropdownOption,
  DropdownOptions,
  DropdownOptionsWithUndefined,
} from '../../lib/Dropdown/types';
import { optionsInclude } from '../../lib/Dropdown/utils';
import { noOp } from '../../lib/utils';
import { Checkbox } from '../Checkbox';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { Tooltip } from '../Tooltip';
import { MultiselectItem } from './MultiselectItem';

export type DropdownProps = {
  /**
   * Initial value for the input
   */
  initialSelection?: DropdownOptions;
  /**
   * Options for the input
   */
  options: DropdownOption[];
  /**
   * SVG icon for visual reference
   * Currently only StepColour utilises this for the colour drops, but can be expanded to other icon types
   */
  icon?: boolean;
  /**
   * Flag for multiselect
   */
  multiselect?: boolean;
  /**
   * maximum count of selected options,
   * only applicable if `multiselect` is `true`
   */
  maxCount?: number;
  /**
   * Flag for typeahead
   */
  typeahead?: boolean;
  /**
   * Typeahead minimum chars
   */
  typeaheadMinChars?: number;
  /**
   * Placeholder of the input
   */
  placeholder?: string;
  /**
   * Flag to disable input
   */
  disabled?: boolean;
  /**
   * Message to display on tooltip for reason of disabled option item
   */
  disabledMessage?: string;
  /**
   * Flag for error
   */
  error?: boolean;
  /**
   * Display with compact styles
   */
  compact?: boolean;
  /**
   * Hide tick icon for selected item
   */
  hideCheckmark?: boolean;
  /**
   * Flag to allow unsetting of value
   */
  allowUnset?: boolean;
  /**
   * Options are sorted alphabetically by default, provide
   * an own sorting function to overwrite this behaviour
   */
  onSort?: (a: DropdownOption, b: DropdownOption) => number;
  /**
   * Change function for dropdown
   */
  onChange?: (selectedOption: DropdownOptionsWithUndefined) => void;
  /**
   * Flag to trigger onChange callback, if an initialSelection is set
   */
  triggerOnChangeForInitialSelection?: boolean;
};

const isMultiselectOption = (
  options: DropdownOptions | null | undefined,
): options is DropdownOption[] => Array.isArray(options);

const CONTAINER = 'relative font-regular w-full';

const BASE_BUTTON = `focus:outline-none w-full border bg-white font-regular flex items-center justify-between text-left rounded`;
const DEFAULT_BUTTON = `${BASE_BUTTON} border-gray-500 text-blue-200 hover:border-blue-100 focus:border-blue-100 focus:shadow-2dp cursor-pointer`;
const DISABLED_BUTTON = `${BASE_BUTTON} border-gray-200 text-gray-400 cursor-default`;
const OPEN_BUTTON = `${BASE_BUTTON} rounded-b-none border-blue-100 hover:text-blue-200 focus:text-blue-200 cursor-pointer`;
const SELECTED_BUTTON = `${BASE_BUTTON} border-gray-500 hover:border-blue-100 focus:border-blue-100 focus:shadow-2dp cursor-pointer`;
const SELECTED_OPEN_BUTTON = `${BASE_BUTTON} rounded-b-none border-blue-100 hover:border-blue-100 focus:border-blue-100 focus:shadow-2dp cursor-pointer`;
const ERROR_BUTTON = `${BASE_BUTTON} border-error text-blue-200 cursor-pointer`;
const ERROR_SELECTED_BUTTON = `${BASE_BUTTON} border-error cursor-pointer`;
const ERROR_OPEN_BUTTON = `${BASE_BUTTON} rounded-b-none border-error hover:text-blue-200 focus:text-blue-200 cursor-pointer`;
const ERROR_SELECTED_OPEN_BUTTON = `${BASE_BUTTON} rounded-b-none border-error hover:text-blue-200 focus:text-blue-200 cursor-pointer`;
const BASE_LIST_WRAPPER = 'absolute w-full z-40';
const BASE_LIST = 'bg-white border rounded-b overflow-y-auto';
const DEFAULT_LIST = `${BASE_LIST} border-blue-100 border-t-0 max-h-[50vh]`;
const LIST_WITH_INPUT = `${BASE_LIST} border-blue-100 border-t-0 max-h-[calc(50vh - 49px)]`;
const ERROR_LIST = `${BASE_LIST} border-error border-t-0`;
const BASE_LIST_ITEM = 'p-3 font-regular flex justify-between focus:outline-none';
const SELECTED_LIST_ITEM = `${BASE_LIST_ITEM} text-blue-100`;
const DEFAULT_LIST_ITEM = `${BASE_LIST_ITEM} text-gray-700 hover:text-blue-100 focus:text-blue-100`;
const DISABLED_LIST_ITEM = `${BASE_LIST_ITEM} text-gray-400 pointer-events-none`;
const LIST_SUBTITLE = 'text-gray-500 text-xs ml-1';

const searchKeys: (keyof DropdownOption)[] = ['name', 'subtitle'];

const searchOptions = {
  keys: searchKeys,
  limit: 100,
  /**
   * `0` = good result,
   * `-Infinity` = bad result
   */
  threshold: -100,
};
const highlightOptions = {
  pre: '<b class="font-semibold">',
  post: '</b>',
};

const getNameForSorting = (item: DropdownOption): string => {
  const displayName = item.name || item.value?.toString();
  return displayName?.toLowerCase();
};

const highlight = <T, K extends keyof T>(
  results: Fuzzysort.KeysResult<T>,
  key: K,
) =>
  fuzzysort.highlight(
    results[(searchOptions.keys as K[]).indexOf(key)],
    highlightOptions.pre,
    highlightOptions.post,
  ) ?? results.obj[key];

export const Dropdown: FC<DropdownProps> = ({
  initialSelection,
  options,
  icon,
  multiselect,
  maxCount = Infinity,
  typeahead,
  typeaheadMinChars = 0,
  placeholder,
  disabled,
  disabledMessage,
  error,
  compact,
  hideCheckmark = false,
  onSort = (a, b) => {
    switch (b.value) {
      case 'notApplicable':
      case 'other':
        return -1;
    }
    switch (a.value) {
      case 'notApplicable':
      case 'other':
        return 1;
      default:
        return getNameForSorting(a).localeCompare(getNameForSorting(b));
    }
  },
  allowUnset,
  onChange = noOp,
  triggerOnChangeForInitialSelection = false,
}: DropdownProps) => {
  const [selection, setSelection] = useState<DropdownOptions>();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  useOnClickOutside(ref, () => setIsOpen(false));
  const [typeaheadValue, setTypeaheadValue] = useState('');

  const isValid = useMemo(
    () => !isMultiselectOption(selection) || selection.length / maxCount <= 1.0,
    [maxCount, selection],
  );
  const isTooShortToShow = useMemo(
    () => !isMultiselectOption(selection) || selection.length / maxCount < 0.5,
    [maxCount, selection],
  );
  const isMaxCountSelected = useMemo(
    () => !isMultiselectOption(selection) || selection.length / maxCount >= 1.0,
    [maxCount, selection],
  );

  placeholder = useMemo(
    () => placeholder || t('placeholders:dropdownDefault'),
    [placeholder, t],
  );

  useEffect(() => {
    if (initialSelection) {
      setSelection(initialSelection);
      if (triggerOnChangeForInitialSelection) {
        onChange(initialSelection);
      }
    } else if (multiselect) {
      setSelection([]);
    } else {
      setSelection(undefined);
    }
  }, [
    initialSelection,
    multiselect,
    onChange,
    triggerOnChangeForInitialSelection,
  ]);

  options = useMemo(() => [...options].sort(onSort), [onSort, options]);

  useEffect(() => {
    (ref?.current as unknown as HTMLElement)?.addEventListener(
      'keyup',
      // Don't import KeyboardEvent from react, this needs to be a non-reacty keyboardevent
      (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
      },
    );
  }, [ref]);

  const showContents = useMemo(
    () => isOpen && !disabled && options?.length > 0,
    [isOpen, disabled, options],
  );

  const buttonClassName = useMemo(() => {
    switch (true) {
      case disabled:
        return DISABLED_BUTTON;
      case error && isOpen && selection && !isMultiselectOption(selection):
        return ERROR_SELECTED_OPEN_BUTTON;
      case error && selection && !isMultiselectOption(selection):
        return ERROR_SELECTED_BUTTON;
      case error && isOpen:
        return ERROR_OPEN_BUTTON;
      case error:
      case !isValid:
        return ERROR_BUTTON;
      case isOpen && selection && !isMultiselectOption(selection):
        return SELECTED_OPEN_BUTTON;
      case selection && !isMultiselectOption(selection):
        return SELECTED_BUTTON;
      case isOpen:
        return OPEN_BUTTON;
      default:
        return DEFAULT_BUTTON;
    }
  }, [disabled, error, isOpen, selection, isValid]);

  const listClassName = useMemo(() => {
    switch (true) {
      case error:
        return ERROR_LIST;
      case typeahead:
        return LIST_WITH_INPUT;
      default:
        return DEFAULT_LIST;
    }
  }, [error, typeahead]);

  const areMultiselectItemsSelected = useMemo(
    () =>
      multiselect && isMultiselectOption(selection) && selection?.length > 0,
    [multiselect, selection],
  );

  const toggleOpen = useCallback(
    () => (disabled ? false : setIsOpen(!isOpen)),
    [disabled, isOpen],
  );

  const isSelectedOption = useCallback(
    (item: DropdownOption) => optionsInclude(item, selection),
    [selection],
  );

  const removeSelection = useCallback(
    (item: DropdownOption) => {
      if (isMultiselectOption(selection)) {
        const newSelection = [...selection].filter(
          ({ value }) => value !== item.value,
        );
        setSelection(newSelection);
        onChange(newSelection);
      }
    },
    [selection, onChange],
  );

  const addSelection = useCallback(
    (item: DropdownOption) => {
      if (isMultiselectOption(selection)) {
        const newSelection = [...selection, item];
        setSelection(newSelection);
        onChange(newSelection);
      }
    },
    [selection, onChange],
  );

  const handleSingleOptionChange = useCallback(
    (item: DropdownOption) => {
      item = options.find(({ value }) => value === item.value) || item;

      const newSelection = isSelectedOption(item) ? undefined : item;
      if (newSelection === undefined && allowUnset === false) return;
      setSelection(newSelection);

      onChange(newSelection);
      toggleOpen();
    },
    [options, isSelectedOption, allowUnset, onChange, toggleOpen],
  );

  const handleMultiOptionChange = useCallback(
    (isChecked: boolean, value: string | number) => {
      if (
        isChecked &&
        isMultiselectOption(selection) &&
        selection.length >= maxCount
      ) {
        // don't add selection, if maxCount is reached
        return false;
      }

      const targetOption = options.find((item) => item.value === value);
      if (targetOption) {
        isChecked ? addSelection(targetOption) : removeSelection(targetOption);
      }
    },
    [addSelection, maxCount, options, removeSelection, selection],
  );

  const renderButton = useMemo(
    () => (
      <button
        data-testid="dropdown"
        className={`${buttonClassName}${
          areMultiselectItemsSelected ? ' pb-2' : ''
        } ${compact ? 'p-2' : 'p-3'}`}
        onClick={toggleOpen}
      >
        {multiselect ? (
          <>
            {areMultiselectItemsSelected ? (
              <div className="flex flex-wrap">
                {(selection as DropdownOption[]).map((item, index) => (
                  <div key={index}>
                    <MultiselectItem
                      item={item}
                      disabled={disabled}
                      onRemove={removeSelection}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="mr-1 text-current">{placeholder}</div>
            )}
            <span
              className="flex-shrink-0 w-6 ml-1"
              style={{ marginTop: '-3px' }}
            >
              <Icon name={isOpen ? 'ArrowUp' : 'ArrowDown'} />
            </span>
            {isMultiselectOption(selection) && maxCount < Infinity && (
              <div
                className={`absolute bottom-1 right-0 text-2xs font-regular mr-1 text-right ${
                  isTooShortToShow && 'invisible'
                } ${!isValid && 'text-red-200'}`}
              >
                {selection.length} / {maxCount}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="mr-2 text-current font-regular truncate">
              {selection ? (selection as DropdownOption).name : placeholder}
            </div>
            <span className="flex-shrink-0 w-6 ml-1">
              {isOpen ? <Icon name="ArrowUp" /> : <Icon name="ArrowDown" />}
            </span>
          </>
        )}
      </button>
    ),
    [
      buttonClassName,
      areMultiselectItemsSelected,
      compact,
      toggleOpen,
      multiselect,
      selection,
      placeholder,
      isOpen,
      maxCount,
      isTooShortToShow,
      isValid,
      disabled,
      removeSelection,
    ],
  );

  const changeTypeaheadValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const filterValue = event?.target?.value;
      setTypeaheadValue(filterValue);
    },
    [],
  );

  const filteredOptions = useMemo((): DropdownOption[] => {
    let result = options.filter((option) => !option?.hidden);

    if (typeahead) {
      if (typeaheadValue.length < typeaheadMinChars) return [];
      if (typeaheadValue.length === 0 && typeaheadMinChars === 0) {
        return result;
      }

      result = fuzzysort.go(typeaheadValue, result, searchOptions).map(
        (results): DropdownOption => ({
          ...results.obj,
          name: highlight(results, 'name'),
          subtitle: highlight(results, 'subtitle'),
        }),
      );
    }
    return result;
  }, [options, typeahead, typeaheadMinChars, typeaheadValue]);

  const renderList = useMemo(() => {
    const startTypingPlaceholder = typeaheadMinChars
      ? t('placeholders:typeToSearch')
      : t('placeholders:typeToFilter');

    return (
      <div data-testid="dropdown-list" className={BASE_LIST_WRAPPER}>
        {typeahead && (
          <div
            data-testid="typeahead-input"
            className="w-full border-blue-200 border-l border-r"
          >
            <Input
              placeholder={startTypingPlaceholder}
              autofocus={true}
              type="text"
              variant="dropdown-filter"
              onChange={changeTypeaheadValue}
              initialValue={typeaheadValue}
            />
          </div>
        )}
        <ul className={listClassName}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((item, index) => {
              const { name, subtitle, value, hexCode } = item;
              const displayName = (
                <span
                  dangerouslySetInnerHTML={{ __html: name || value.toString() }}
                />
              );
              const displaySubtitle = subtitle && (
                <p
                  className={LIST_SUBTITLE}
                  dangerouslySetInnerHTML={{ __html: subtitle }}
                />
              );
              const isNotSelectableMultiSelect =
                isMaxCountSelected && !isSelectedOption(item) && item.disabled;

              const disabledListItemStyling = item.disabled
                ? DISABLED_LIST_ITEM
                : '';

              const selectedListItemStyling = isSelectedOption(item)
                ? SELECTED_LIST_ITEM
                : '';

              const listItemClassName =
                !item.disabled && !isSelectedOption(item)
                  ? DEFAULT_LIST_ITEM
                  : disabledListItemStyling || selectedListItemStyling;

              return multiselect ? (
                <li
                  data-testid="dropdown-option"
                  key={index}
                  className="p-3 hover:blue-text-100"
                >
                  <Checkbox
                    initialChecked={
                      isMultiselectOption(selection) &&
                      !!selection.find(
                        (selectedOption) => selectedOption.value === value,
                      )
                    }
                    value={value}
                    disabled={disabled || isNotSelectableMultiSelect}
                    onChange={handleMultiOptionChange}
                  >
                    {icon && (
                      <div className="w-6 mr-2 h-6" style={{ fill: hexCode }}>
                        <ColourDrop fill={hexCode} />
                      </div>
                    )}
                    {displayName}
                    {displaySubtitle}
                  </Checkbox>
                </li>
              ) : (
                <li
                  data-testid="dropdown-option"
                  className="relative"
                  key={index}
                  data-tip={item.disabled ? disabledMessage : undefined}
                >
                  <a
                    href="#"
                    className={listItemClassName}
                    onClick={(event: MouseEvent) => {
                      event.preventDefault();
                      handleSingleOptionChange(item);
                    }}
                    // Don't import KeyboardEvent from react, the above addEventListener for esc needs to be a non-reacty keyboardevent
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event?.keyCode === 32) {
                        handleSingleOptionChange(item);
                      }
                    }}
                  >
                    <span
                      className={`text-current font-regular ${
                        isSelectedOption(item) && 'mr-1'
                      }`}
                    >
                      {displayName}
                      {displaySubtitle}
                    </span>
                    {isSelectedOption(item) && !hideCheckmark && (
                      <span className="flex-shrink-0 w-6 ml-1">
                        <Icon name="Checkmark" />
                      </span>
                    )}
                  </a>

                  <Tooltip place="top" />
                </li>
              );
            })
          ) : typeaheadValue.length < typeaheadMinChars ? (
            <></>
          ) : (
            <li className="p-2 text-gray-500">{t('labels:noOptions')}</li>
          )}
        </ul>
      </div>
    );
  }, [
    typeaheadMinChars,
    t,
    typeahead,
    icon,
    changeTypeaheadValue,
    typeaheadValue,
    listClassName,
    filteredOptions,
    isMaxCountSelected,
    isSelectedOption,
    multiselect,
    selection,
    disabled,
    handleMultiOptionChange,
    hideCheckmark,
    handleSingleOptionChange,
    disabledMessage,
  ]);

  return (
    <div className={CONTAINER} ref={ref}>
      {renderButton}
      {showContents ? renderList : null}
    </div>
  );
};
